import styled, { css } from 'styled-components';
import { variants, th, zIndexes } from 'style/theme';
import { NavLink } from 'react-router-dom';
import { FaCaretDown } from 'react-icons/fa';
import { A } from 'components/Typography';
import Avatar from 'products/global/views/Profile/components/CustomizeAvatar/components/Avatar';

export const Icon = styled(FaCaretDown)`
  color: inherit;
  margin-left: 8px;
  transition: transform 0.3s;
`;

export const StyledAvatar = styled(Avatar)`
  top: 3px;
`;

export const AvatarWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;

export const AvatarContainer = styled.div`
  width: 34px;
  height: 34px;
  background-color: ${({ background }) => background || 'transparent'};
  background-image: url(${({ src }) => src});
  background-size: cover;
  border-radius: 50%;
  margin-right: 10px;
  position: relative;

  ::before {
    content: '';
    transition: 0.3s;
    z-index: -1;
    position: absolute;
    top: 0px;
    left: 0px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background-color: ${th('colors.white')};
  }
`;

export const Label = styled.span`
  ${variants('variant', {
    dark: css`
      color: ${th('colors.black')};
    `,
    light: css`
      color: ${th('colors.white')};
    `
  })};

  ${({ labelColor }) =>
    labelColor &&
    css`
      color: ${th(`colors.${labelColor}`)};
    `};

  display: flex;
  flex-direction: row;
  align-items: center;
`;

const linkStyles = css`
  ${variants('variant', {
    dark: css`
      color: ${th('colors.white')};

      :hover {
        color: ${th('colors.transparentWhite.7')};
      }
    `,
    light: css`
      color: ${th('colors.black')};

      :hover {
        color: ${th('colors.transparentBlack.7')};
      }
    `
  })}

  > img {
    width: 16px;
    margin-right: 10px;
  }

  display: flex;
  align-items: center;
  text-decoration: none;
  line-height: 38px;
`;

export const Link = styled(NavLink)`
  ${linkStyles};
`;

export const ExternalLink = styled(A)`
  ${linkStyles};
`;

export const EventLink = styled.div`
  color: ${({ color }) => (color ? th(`colors.${color}`) : th('colors.white'))};
  display: flex;
  align-items: center;

  :hover {
    color: ${({ hoverColor }) =>
      hoverColor ? th(`colors.${hoverColor}`) : th('colors.transparentWhite.7')};
  }

  > img {
    width: 16px;
    margin-right: 10px;
  }

  line-height: 38px;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 60px;
  margin-right: 16px;
  margin-top: 6px;
`;

export const LinkIcon = styled.img`
  width: ${({ imageWidth }) => `${imageWidth}px`};
`;

export const LinkTitle = styled.div`
  color: ${({ $color }) => $color};
  margin-bottom: 4px;
`;

export const LinkSub = styled.div`
  font-weight: normal;
`;

const largeLinkStyles = css`
  display: flex;
  align-items: flex-start;
  text-decoration: none;
  color: ${th('colors.white')};

  :not(:last-child) {
    margin-bottom: 20px;
  }
`;

export const LargeLink = styled(NavLink)`
  ${largeLinkStyles};
`;

export const LargeExternalLink = styled.a.attrs({
  target: '_blank',
  rel: 'noopener noreferrer'
})`
  ${largeLinkStyles};
`;

export const Dropdown = styled.div`
  ${variants('variant', {
    dark: css`
      background-color: ${th('colors.darkSlateGray')};
    `,
    light: css`
      background-color: ${th('colors.white')};
    `
  })}

  ${variants('position', {
    left: css`
      right: 0;

      &::after {
        right: 42px;
      }
    `,
    right: css`
      left: -10%;

      &::after {
        left: 42px;
      }
    `
  })}

  ${variants('size', {
    normal: css`
      width: 225px;
      padding: 10px 16px;

      &::after {
        border-color: transparent transparent
          ${(p) =>
            p.variant === 'light' ? th('colors.white') : th('colors.darkSlateGray')}
          transparent;
      }
    `,
    large: css`
      width: 358px;
      padding: 24px 20px;
      background-color: ${th('colors.darkSlateGray')};

      &::after {
        border-color: transparent transparent ${th('colors.darkSlateGray')} transparent;
      }
    `
  })}

  position: absolute;
  top: 55px;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  transition: opacity 0.2s;
  box-shadow: ${th('shadows.small')};
  z-index: ${zIndexes.navigation};

  &::after {
    content: ' ';
    position: absolute;
    bottom: 100%;
    border-width: 8px;
    border-style: solid;
  }
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  color: ${th('colors.white')};
  font-weight: bold;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: opacity 0.2s;

  > ${Dropdown} {
    opacity: 0;
    pointer-events: none;
  }

  &:hover {
    color: ${th('colors.transparentWhite.5')};

    > ${({ disabled }) => !disabled && Dropdown} {
      opacity: 1;
      pointer-events: initial;
    }

    > ${Label} > ${Icon} {
      transform: rotate(180deg);
    }

    ${AvatarContainer}::before {
      top: -2px;
      left: -2px;
      width: 38px;
      height: 38px;
    }
  }
`;
